/**
 * Allows handling wishlist states in product cards.
 *
 * Usage:
 * ```
 * <product-card>
 *  ...rest of card
 *  <button class="product-card--wishlist" data-product-id="123" data-variant-id="456">
 *    <span class="wishlist-icon-inactive">{%- render 'svg-icons' with 'wishlist' -%}</span>
 *    <span class="wishlist-icon-active">{%- render 'svg-icons' with 'wishlist-filled' -%}</span>
 *  </button>
 *  ...rest of card
 * </product-card>
 * ```
 *
 * For `data-variant-id`, add the first variant.
 */

import {
  getWishlists,
  addProductToWishlist,
  getOrCreateDefaultWishlist,
  removeFromWishlist,
} from "@app/scripts/api/wishlist"
import { selectDefaultWishlist } from "@app/scripts/utils"
import { IWishlist, IWishlistItem } from "@app/types/wishlist"

const owner = window.ShopifyAnalytics.meta.page.customerId?.toString() || null
const attachedListeners = new WeakSet<HTMLElement>()
let wishlist: IWishlist | null = null

const handleWishlistAction = async (
  cardElement: HTMLElement,
  wishlist: IWishlist,
  wishlistItem: IWishlistItem | undefined,
  owner: string
) => {
  const productId = wishlistItem?.product.id
    ? String(wishlistItem?.product.id)
    : cardElement.dataset.productId || ""
  const variantId = wishlistItem?.product.variants[0].id
    ? String(wishlistItem?.product.variants[0].id)
    : cardElement.dataset.variantId || ""

  if (!wishlist?.id) {
    console.error("Wishlist ID not found.")
    return
  }

  const isActive =
    cardElement.classList.contains("product-card--wishlist--active") &&
    wishlistItem

  try {
    if (isActive) {
      await removeFromWishlist(
        wishlistItem.id,
        productId,
        variantId,
        wishlist.id
      )
    } else {
      await addProductToWishlist({
        owner,
        product_id: productId,
        variant_id: variantId,
        wishlist_ids: [wishlist.id],
      })
    }

    cardElement.classList.toggle("product-card--wishlist--active")
    await getWishlists(owner, false)
  } catch (error) {
    console.error("Error during wishlist action:", error)
  }

  // Hack so that page reloads when clicking on a wishlist icons in a carousel within the wishlist page.
  if (window.location.pathname.includes("/pages/wishlist")) {
    window.location.reload()
  }
}

const setupEventListenersForCard = (cardElement: HTMLElement) => {
  // Ensure listener is not added multiple times
  if (attachedListeners.has(cardElement)) return
  attachedListeners.add(cardElement)

  cardElement.addEventListener("click", async () => {
    if (!owner) {
      window.location.href =
        "/account/login?return_url=" +
        encodeURIComponent(window.location.pathname)
      return
    }

    if (!wishlist) {
      wishlist = await getOrCreateDefaultWishlist(owner)
    }
    if (!wishlist) {
      console.error("No wishlist found or could not be created")
      return
    }

    // Re-fetch on every click so add/remove decisions reflect current state
    // rather than the snapshot taken when the listener was first attached.
    const fresh = await getWishlists(owner, false)
    const freshDefault =
      selectDefaultWishlist(fresh?.wishlists || []) ?? wishlist
    wishlist = freshDefault

    const { productId } = cardElement.dataset
    const wishlistItem = freshDefault?.items?.item?.find(
      (item) => String(item.product.id) === productId
    )

    await handleWishlistAction(cardElement, freshDefault, wishlistItem, owner)
  })
}

const processProductCards = (
  cards: NodeListOf<Element> | Element[],
  wishlist: IWishlist | null
) => {
  cards.forEach((card) => {
    const cardElement = card as HTMLElement
    const { productId } = cardElement.dataset
    const wishlistItem = wishlist?.items?.item?.find(
      (item) => String(item.product.id) === productId
    )

    const isInWishlist = !!wishlistItem
    cardElement.classList.toggle("product-card--wishlist--active", isInWishlist)
    setupEventListenersForCard(cardElement)
  })
}

/**
 * Mainly used for the wishlist icon in the header.
 * The icon is reactive and will update its state based on if the user has products in their wishlist.
 */
const setupReactiveWishlistIcon = () => {
  document.addEventListener("wishlist-items:update", async () => {
    if (!owner) return
    const wishlists = await getWishlists(owner, false)
    const wishlist = selectDefaultWishlist(wishlists?.wishlists || [])

    const hasProductsInWishlist = !!wishlist?.items?.item?.length

    document.querySelectorAll(".wishlist-icon-reactive").forEach((icon) => {
      icon
        .querySelector(".wishlist-filled")
        ?.classList.toggle("is-hidden", !hasProductsInWishlist)
      icon
        .querySelector(".wishlist-empty")
        ?.classList.toggle("is-hidden", hasProductsInWishlist)
    })
  })
}

document.addEventListener("DOMContentLoaded", async () => {
  if (owner) {
    const wishlists = await getWishlists(owner, true)
    wishlist = selectDefaultWishlist(wishlists?.wishlists || [])
    console.assert(wishlist, "No default wishlist found for the current owner.")

    if (wishlist) {
      setupReactiveWishlistIcon()
      document.dispatchEvent(new Event("wishlist-items:update"))
    }
  }

  const initialProductCards = document.querySelectorAll(
    ".product-card--wishlist"
  )
  processProductCards(initialProductCards, wishlist)

  // Observe for dynamically added cards
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const elementNode = node as Element
            const cardsToProcess: HTMLElement[] = []

            // Check if the added node itself is a product card
            if (elementNode.matches(".product-card--wishlist")) {
              cardsToProcess.push(elementNode as HTMLElement)
            }

            // Also check for product cards within added subtrees
            const newCardsInSubtree = elementNode.querySelectorAll<HTMLElement>(
              ".product-card--wishlist"
            )
            if (newCardsInSubtree.length > 0) {
              // Add only unique cards
              Array.from(newCardsInSubtree).forEach((newCard) => {
                if (!cardsToProcess.includes(newCard)) {
                  cardsToProcess.push(newCard)
                }
              })
            }

            if (cardsToProcess.length > 0) {
              processProductCards(cardsToProcess, wishlist)
            }
          }
        })
      }
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })
})
